import {
  AddToCartItem,
  ShopifyCart,
  ShopifyCartItem,
  ShopifyCartService,
} from '../types/servicesShopifyCart';

export class ShopifyCartRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export const useShopifyCartService = (): ShopifyCartService => {
  // default blank state
  const DEFAULT_EMPTY_CART = (): ShopifyCart => ({
    token: '',
    note: null,
    attributes: {},
    original_total_price: 0,
    total_price: 0,
    total_discount: 0,
    total_weight: 0,
    item_count: 0,
    items: [],
    requires_shipping: false,
    currency: 'USD',
    items_subtotal_price: 0,
    cart_level_discount_applications: [],
  });

  const DEFAULT_CART_ITEM: ShopifyCartItem = {
    id: 0,
    quantity: 0,
    variant_id: 0,
    key: '',
    title: '',
    price: 0,
    original_price: 0,
    discounted_price: 0,
    line_price: 0,
    original_line_price: 0,
    total_discount: 0,
    discounts: [],
    sku: '',
    grams: 0,
    vendor: '',
    properties: {},
    product_id: 0,
    image: '',
    handle: '',
    requires_shipping: false,
    product_type: '',
    product_title: '',
    product_description: '',
    variant_title: '',
    variant_options: [],
    options_with_values: [],
    line_level_discount_allocations: [],
  };

  // handle api errors for below ones
  const handleApiError = (error: unknown): ShopifyCart => {
    console.error('Shopify API Error:', error);
    return DEFAULT_EMPTY_CART();
  };

  const getCart = async (): Promise<ShopifyCart> => {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.statusText}`);
    }
    const data = await response.json();
    return data as ShopifyCart;
  };

  const updateCart = async (updates: Record<string, number>): Promise<ShopifyCart> => {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
    });
    if (!response.ok) {
      throw new ShopifyCartRequestError(
        `Failed to update cart: ${response.statusText}`,
        response.status,
      );
    }
    const data = await response.json();
    return data as ShopifyCart;
  };

  const updateCartAttributes = async (attributes: Record<string, string>): Promise<ShopifyCart> => {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ attributes }),
    });
    if (!response.ok) {
      throw new ShopifyCartRequestError(
        `Failed to update cart attributes: ${response.statusText}`,
        response.status,
      );
    }
    const data = await response.json();
    return data as ShopifyCart;
  };

  const addToCart = async (items: AddToCartItem[]): Promise<ShopifyCart> => {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    });
    if (!response.ok) {
      throw new ShopifyCartRequestError(
        `Failed to add items to cart: ${response.statusText}`,
        response.status,
      );
    }
    const data = await response.json();
    return data as ShopifyCart;
  };

  const changeCartItem = async (key: string, quantity: number): Promise<ShopifyCart> => {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!response.ok) {
      throw new ShopifyCartRequestError(
        `Failed to change cart item: ${response.statusText}`,
        response.status,
      );
    }
    const data = await response.json();
    return data as ShopifyCart;
  };

  const deleteCartItem = async (key: string): Promise<ShopifyCart> => {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: 0,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete cart item: ${response.statusText}`);
      }

      const data = await response.json();
      return data as ShopifyCart;
    } catch (error) {
      return handleApiError(error);
    }
  };

  const clearCart = async (): Promise<ShopifyCart> => {
    const response = await fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to clear cart: ${response.statusText}`);
    }

    const data = await response.json();
    return data as ShopifyCart;
  };

  const applyDiscount = async (discountCode: string): Promise<ShopifyCart> => {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ discount: discountCode }),
      });
      if (!response.ok) {
        throw new Error(`Failed to apply discount: ${response.statusText}`);
      }
      const data = await response.json();
      return data as ShopifyCart;
    } catch (error) {
      return handleApiError(error);
    }
  };

  return {
    DEFAULT_EMPTY_CART,
    DEFAULT_CART_ITEM,
    getCart,
    updateCart,
    updateCartAttributes,
    addToCart,
    changeCartItem,
    deleteCartItem,
    clearCart,
    applyDiscount,
  };
};
